// src/components/leaves/LeaveTable.js
import React, { useState, useMemo } from 'react';

const LeaveTable = ({ leaveRequests, onApprove, onCancel, isAdmin, currentUserId, userType }) => {
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approvalData, setApprovalData] = useState({
        status: 'approved',
        comments: ''
    });

    // Filter and sort leave requests
    const filteredAndSortedRequests = useMemo(() => {
        let filtered = leaveRequests;

        // Apply search filter
        if (searchTerm) {
            filtered = leaveRequests.filter(request =>
                request.staff_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.staff_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.leave_type_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.premises_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply sorting
        if (sortField) {
            filtered = [...filtered].sort((a, b) => {
                const aVal = a[sortField];
                const bVal = b[sortField];

                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;

                let comparison = 0;
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    comparison = aVal - bVal;
                } else {
                    comparison = aVal.toString().localeCompare(bVal.toString());
                }

                return sortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return filtered;
    }, [leaveRequests, searchTerm, sortField, sortDirection]);

    // Pagination
    const totalPages = Math.ceil(filteredAndSortedRequests.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedRequests = filteredAndSortedRequests.slice(startIndex, endIndex);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleApprovalAction = (request, action) => {
        setSelectedRequest(request);
        setApprovalData({
            status: action,
            comments: ''
        });
        setShowApprovalModal(true);
    };

    const handleApprovalSubmit = async () => {
        try {
            await onApprove(selectedRequest.id, approvalData.status, approvalData.comments);
            setShowApprovalModal(false);
            setSelectedRequest(null);
            setApprovalData({ status: 'approved', comments: '' });
        } catch (err) {
            console.error('Error processing approval:', err);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved': return '#10b981';
            case 'rejected': return '#ef4444';
            case 'pending': return '#f59e0b';
            case 'cancelled': return '#6b7280';
            default: return '#6b7280';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved': return 'fas fa-check-circle';
            case 'rejected': return 'fas fa-times-circle';
            case 'pending': return 'fas fa-clock';
            case 'cancelled': return 'fas fa-ban';
            default: return 'fas fa-question-circle';
        }
    };

    const canUserTakeAction = (request) => {
        // Admin can take action on any pending request
        if (isAdmin && request.status === 'pending') {
            return true;
        }
        // Staff can only cancel their own requests if they're pending or approved
        if (!isAdmin && request.user_id === currentUserId &&
            (request.status === 'pending' || request.status === 'approved')) {
            return 'cancel';
        }
        return false;
    };

    const showOrganizationColumn = userType === 'core_admin';
    const showPremisesColumn = userType === 'core_admin' || userType === 'client_admin';

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    };

    if (!leaveRequests || leaveRequests.length === 0) {
        return (
            <div className="leave-table-container">
                <div className="empty-state">
                    <i className="fas fa-calendar-times"></i>
                    <h3>No Leave Requests</h3>
                    <p>No leave requests found. Apply for leave to get started.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="leave-table-container">
            {/* Table Header */}
            <div className="table-header">
                <div className="table-controls-left">
                    <div className="search-box">
                        <i className="fas fa-search"></i>
                        <input
                            type="text"
                            placeholder="Search leave requests..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>

                    <div className="page-size-selector">
                        <label>Show:</label>
                        <select value={pageSize} onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>

                <div className="table-controls-right">
                    <div className="results-info">
                        Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedRequests.length)} of {filteredAndSortedRequests.length} requests
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="table-wrapper">
                <table className="leave-table">
                    <thead>
                        <tr>
                            <th
                                className="sortable"
                                onClick={() => handleSort('staff_name')}
                            >
                                Staff Name
                                <i className={`fas fa-sort${sortField === 'staff_name'
                                        ? sortDirection === 'asc' ? '-up' : '-down'
                                        : ''
                                    }`}></i>
                            </th>
                            <th
                                className="sortable"
                                onClick={() => handleSort('leave_type_name')}
                            >
                                Leave Type
                                <i className={`fas fa-sort${sortField === 'leave_type_name'
                                        ? sortDirection === 'asc' ? '-up' : '-down'
                                        : ''
                                    }`}></i>
                            </th>
                            {showOrganizationColumn && (
                                <th
                                    className="sortable"
                                    onClick={() => handleSort('organization_name')}
                                >
                                    Organization
                                    <i className={`fas fa-sort${sortField === 'organization_name'
                                            ? sortDirection === 'asc' ? '-up' : '-down'
                                            : ''
                                        }`}></i>
                                </th>
                            )}
                            {showPremisesColumn && (
                                <th
                                    className="sortable"
                                    onClick={() => handleSort('premises_name')}
                                >
                                    Premises
                                    <i className={`fas fa-sort${sortField === 'premises_name'
                                            ? sortDirection === 'asc' ? '-up' : '-down'
                                            : ''
                                        }`}></i>
                                </th>
                            )}
                            <th
                                className="sortable"
                                onClick={() => handleSort('start_date')}
                            >
                                Start Date
                                <i className={`fas fa-sort${sortField === 'start_date'
                                        ? sortDirection === 'asc' ? '-up' : '-down'
                                        : ''
                                    }`}></i>
                            </th>
                            <th
                                className="sortable"
                                onClick={() => handleSort('end_date')}
                            >
                                End Date
                                <i className={`fas fa-sort${sortField === 'end_date'
                                        ? sortDirection === 'asc' ? '-up' : '-down'
                                        : ''
                                    }`}></i>
                            </th>
                            <th
                                className="sortable"
                                onClick={() => handleSort('total_days')}
                            >
                                Days
                                <i className={`fas fa-sort${sortField === 'total_days'
                                        ? sortDirection === 'asc' ? '-up' : '-down'
                                        : ''
                                    }`}></i>
                            </th>
                            <th
                                className="sortable"
                                onClick={() => handleSort('status')}
                            >
                                Status
                                <i className={`fas fa-sort${sortField === 'status'
                                        ? sortDirection === 'asc' ? '-up' : '-down'
                                        : ''
                                    }`}></i>
                            </th>
                            <th>Reason</th>
                            <th
                                className="sortable"
                                onClick={() => handleSort('created_at')}
                            >
                                Applied
                                <i className={`fas fa-sort${sortField === 'created_at'
                                        ? sortDirection === 'asc' ? '-up' : '-down'
                                        : ''
                                    }`}></i>
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRequests.map((request) => {
                            const actionType = canUserTakeAction(request);

                            return (
                                <tr key={request.id}>
                                    <td>
                                        <div className="staff-info">
                                            <div className="staff-name">{request.staff_name}</div>
                                            <div className="staff-id">ID: {request.staff_code || request.staff_id}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="leave-type">{request.leave_type_name}</span>
                                    </td>
                                    {showOrganizationColumn && <td>{request.organization_name || '-'}</td>}
                                    {showPremisesColumn && <td>{request.premises_name || '-'}</td>}
                                    <td>{formatDate(request.start_date)}</td>
                                    <td>{formatDate(request.end_date)}</td>
                                    <td>
                                        <span className="days-count">{request.total_days}</span>
                                    </td>
                                    <td>
                                        <span
                                            className="status-badge"
                                            style={{ backgroundColor: getStatusColor(request.status) }}
                                        >
                                            <i className={getStatusIcon(request.status)}></i>
                                            {request.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="reason-cell">
                                            <div className="reason-text" title={request.reason}>
                                                {request.reason.length > 50
                                                    ? request.reason.substring(0, 50) + '...'
                                                    : request.reason
                                                }
                                            </div>
                                        </div>
                                    </td>
                                    <td>{formatDate(request.created_at)}</td>
                                    <td>
                                        <div className="action-buttons">
                                            {actionType === true && (
                                                <>
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        onClick={() => handleApprovalAction(request, 'approved')}
                                                        title="Approve"
                                                    >
                                                        <i className="fas fa-check"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleApprovalAction(request, 'rejected')}
                                                        title="Reject"
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </>
                                            )}
                                            {actionType === 'cancel' && (
                                                <button
                                                    className="btn btn-sm btn-warning"
                                                    onClick={() => onCancel(request.id)}
                                                    title="Cancel Request"
                                                >
                                                    <i className="fas fa-ban"></i>
                                                    Cancel
                                                </button>
                                            )}
                                            {request.attachment && (
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => window.open(request.attachment, '_blank')}
                                                    title="View Attachment"
                                                >
                                                    <i className="fas fa-paperclip"></i>
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-sm btn-info"
                                                title="View Details"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        className="pagination-btn"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    >
                        <i className="fas fa-chevron-left"></i>
                        Previous
                    </button>

                    {getPageNumbers().map(page => (
                        <button
                            key={page}
                            className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        className="pagination-btn"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    >
                        Next
                        <i className="fas fa-chevron-right"></i>
                    </button>
                </div>
            )}

            {/* Approval Modal */}
            {showApprovalModal && selectedRequest && (
                <div className="modal-overlay">
                    <div className="approval-modal">
                        <div className="modal-header">
                            <h3>
                                {approvalData.status === 'approved' ? 'Approve' : 'Reject'} Leave Request
                            </h3>
                            <button
                                className="close-btn"
                                onClick={() => setShowApprovalModal(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="request-summary">
                                <h4>Leave Request Details</h4>
                                <div className="summary-grid">
                                    <div className="summary-item">
                                        <span className="label">Staff:</span>
                                        <span className="value">{selectedRequest.staff_name}</span>
                                    </div>
                                    <div className="summary-item">
                                        <span className="label">Leave Type:</span>
                                        <span className="value">{selectedRequest.leave_type_name}</span>
                                    </div>
                                    <div className="summary-item">
                                        <span className="label">Period:</span>
                                        <span className="value">
                                            {formatDate(selectedRequest.start_date)} - {formatDate(selectedRequest.end_date)}
                                        </span>
                                    </div>
                                    <div className="summary-item">
                                        <span className="label">Duration:</span>
                                        <span className="value">{selectedRequest.total_days} days</span>
                                    </div>
                                    <div className="summary-item full-width">
                                        <span className="label">Reason:</span>
                                        <span className="value">{selectedRequest.reason}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="approval-form">
                                <div className="form-group">
                                    <label>Comments {approvalData.status === 'rejected' ? '*' : '(Optional)'}</label>
                                    <textarea
                                        className="form-input"
                                        value={approvalData.comments}
                                        onChange={(e) => setApprovalData(prev => ({
                                            ...prev,
                                            comments: e.target.value
                                        }))}
                                        placeholder={approvalData.status === 'approved'
                                            ? "Add any comments for the approval..."
                                            : "Please provide a reason for rejection..."
                                        }
                                        rows="4"
                                        required={approvalData.status === 'rejected'}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowApprovalModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className={`btn ${approvalData.status === 'approved' ? 'btn-success' : 'btn-danger'}`}
                                onClick={handleApprovalSubmit}
                                disabled={approvalData.status === 'rejected' && !approvalData.comments.trim()}
                            >
                                <i className={`fas ${approvalData.status === 'approved' ? 'fa-check' : 'fa-times'}`}></i>
                                {approvalData.status === 'approved' ? 'Approve Request' : 'Reject Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .leave-table-container {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    overflow: hidden;
                }

                .table-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-lg);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                    flex-wrap: wrap;
                    gap: var(--spacing-md);
                }

                .table-controls-left {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-md);
                    flex-wrap: wrap;
                }

                .search-box {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .search-box i {
                    position: absolute;
                    left: 12px;
                    color: var(--text-light);
                    font-size: 0.9rem;
                }

                .search-box input {
                    padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) 35px;
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    width: 300px;
                }

                .page-size-selector {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    font-size: 0.9rem;
                }

                .page-size-selector select {
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-sm);
                    font-size: 0.9rem;
                }

                .results-info {
                    font-size: 0.85rem;
                    color: var(--text-light);
                }

                .table-wrapper {
                    overflow-x: auto;
                }

                .leave-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .leave-table th {
                    background: var(--light-color);
                    padding: var(--spacing-md);
                    text-align: left;
                    font-weight: 600;
                    color: var(--text-dark);
                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                    font-size: 0.85rem;
                    white-space: nowrap;
                }

                .leave-table th.sortable {
                    cursor: pointer;
                    user-select: none;
                    transition: background-color 0.2s ease;
                }

                .leave-table th.sortable:hover {
                    background: #e9ecef;
                }

                .leave-table th i {
                    margin-left: var(--spacing-xs);
                    color: var(--text-light);
                    font-size: 0.7rem;
                }

                .leave-table td {
                    padding: var(--spacing-md);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                    font-size: 0.85rem;
                    vertical-align: top;
                }

                .leave-table tr:hover {
                    background: rgba(var(--admin-primary-rgb), 0.02);
                }

                .staff-info {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xs);
                }

                .staff-name {
                    font-weight: 600;
                    color: var(--text-dark);
                }

                .staff-id {
                    font-size: 0.75rem;
                    color: var(--text-light);
                }

                .leave-type {
                    background: rgba(var(--admin-primary-rgb), 0.1);
                    color: var(--admin-primary);
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    font-size: 0.75rem;
                    font-weight: 500;
                }

                .days-count {
                    font-weight: 600;
                    color: var(--text-dark);
                }

                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: var(--spacing-xs);
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: capitalize;
                    color: white;
                }

                .reason-cell {
                    max-width: 200px;
                }

                .reason-text {
                    line-height: 1.3;
                    color: var(--text-dark);
                }

                .action-buttons {
                    display: flex;
                    gap: var(--spacing-xs);
                    flex-wrap: wrap;
                }

                .btn {
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border: none;
                    border-radius: var(--radius-sm);
                    font-size: 0.75rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-xs);
                    white-space: nowrap;
                }

                .btn-sm {
                    padding: var(--spacing-xs);
                    min-width: 28px;
                    height: 28px;
                    justify-content: center;
                }

                .btn-success {
                    background: var(--success);
                    color: white;
                }

                .btn-success:hover {
                    background: #059669;
                }

                .btn-danger {
                    background: var(--error);
                    color: white;
                }

                .btn-danger:hover {
                    background: #c53030;
                }

                .btn-warning {
                    background: var(--warning);
                    color: white;
                    padding: var(--spacing-xs) var(--spacing-sm);
                    min-width: auto;
                    height: auto;
                }

                .btn-warning:hover {
                    background: #d97706;
                }

                .btn-secondary {
                    background: var(--light-color);
                    color: var(--text-dark);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                .btn-secondary:hover {
                    background: #e9ecef;
                }

                .btn-info {
                    background: #0ea5e9;
                    color: white;
                }

                .btn-info:hover {
                    background: #0284c7;
                }

                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: var(--spacing-xs);
                    padding: var(--spacing-lg);
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                }

                .pagination-btn {
                    padding: var(--spacing-sm) var(--spacing-md);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--white);
                    color: var(--text-dark);
                    border-radius: var(--radius-sm);
                    cursor: pointer;
                    font-size: 0.85rem;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-xs);
                    transition: all 0.2s ease;
                }

                .pagination-btn:hover:not(:disabled) {
                    background: var(--light-color);
                    border-color: var(--admin-primary);
                }

                .pagination-btn.active {
                    background: var(--admin-primary);
                    color: white;
                    border-color: var(--admin-primary);
                }

                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .empty-state {
                    text-align: center;
                    padding: var(--spacing-xl) * 2;
                    color: var(--text-light);
                }

                .empty-state i {
                    font-size: 3rem;
                    margin-bottom: var(--spacing-lg);
                    color: var(--admin-primary);
                    opacity: 0.5;
                }

                .empty-state h3 {
                    margin-bottom: var(--spacing-sm);
                    color: var(--text-dark);
                }

                /* Approval Modal */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: var(--spacing-md);
                }

                .approval-modal {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-lg);
                    width: 100%;
                    max-width: 600px;
                    max-height: 90vh;
                    overflow: hidden;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-xl);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                }

                .modal-header h3 {
                    margin: 0;
                    color: var(--text-dark);
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    color: var(--text-light);
                    padding: var(--spacing-xs);
                }

                .modal-body {
                    padding: var(--spacing-xl);
                    overflow-y: auto;
                }

                .request-summary {
                    margin-bottom: var(--spacing-xl);
                }

                .request-summary h4 {
                    margin-bottom: var(--spacing-md);
                    color: var(--text-dark);
                }

                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: var(--spacing-md);
                }

                .summary-item {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xs);
                    padding: var(--spacing-sm);
                    background: var(--light-color);
                    border-radius: var(--radius-md);
                }

                .summary-item.full-width {
                    grid-column: 1 / -1;
                }

                .summary-item .label {
                    font-size: 0.8rem;
                    color: var(--text-light);
                    font-weight: 500;
                }

                .summary-item .value {
                    font-size: 0.9rem;
                    color: var(--text-dark);
                    font-weight: 600;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-sm);
                }

                .form-group label {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .form-input {
                    padding: var(--spacing-md);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    resize: vertical;
                }

                .form-input:focus {
                    outline: none;
                    border-color: var(--admin-primary);
                }

                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: var(--spacing-sm);
                    padding: var(--spacing-xl);
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                }

                .modal-actions .btn {
                    padding: var(--spacing-sm) var(--spacing-lg);
                    font-size: 0.9rem;
                    min-width: 120px;
                    justify-content: center;
                }

                @media (max-width: 768px) {
                    .table-header {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .table-controls-left {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .search-box input {
                        width: 100%;
                    }

                    .leave-table {
                        font-size: 0.8rem;
                    }

                    .leave-table th,
                    .leave-table td {
                        padding: var(--spacing-sm);
                    }

                    .action-buttons {
                        flex-direction: column;
                    }

                    .summary-grid {
                        grid-template-columns: 1fr;
                    }

                    .modal-actions {
                        flex-direction: column;
                    }

                    .modal-actions .btn {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default LeaveTable;
